import { BALANCE, DIFFICULTY } from '../balance.ts';
import type { ActContext, GenContext, TickContext } from '../puzzles/types.ts';
import { getMap } from '../maps/graph.ts';
import type { Rng } from '../rng.ts';
import type {
  ExecState,
  GameState,
  Phase,
  PlayerId,
  PuzzleInstance,
  PuzzleType,
  RoomId,
  Stop,
} from '../types.ts';
import { emit, playerOf, presentRoles, puzzlePlayers, type Ctx } from './context.ts';
import { computeResult } from './outcome.ts';

/** True once the heist has ended (failed, or completed through to the reveal). */
export function heistOver(s: GameState): boolean {
  return s.phase === 'reveal' || s.phase === 'ended';
}

/** Changes the phase and tells everyone. */
export function setPhase(ctx: Ctx, phase: Phase): void {
  ctx.s.phase = phase;
  ctx.s.phaseStartedMs = ctx.s.clockMs;
  emit(ctx, 'phase', { phase });
}

export function currentStop(exec: ExecState): Stop | undefined {
  return exec.itinerary[exec.stopIndex];
}

/** Ids of the puzzles the team can work on right now. */
export function activePuzzleIds(s: GameState): string[] {
  const exec = s.exec;
  if (!exec) return [];
  if (s.phase === 'escape') return exec.escapePuzzle ? [exec.escapePuzzle] : [];
  const stop = currentStop(exec);
  if (!stop) return [];
  if (stop.kind === 'room') return stop.puzzles;
  if (stop.kind === 'corridor' && stop.puzzle) return [stop.puzzle];
  return [];
}

export function activePuzzles(s: GameState): PuzzleInstance[] {
  const exec = s.exec;
  if (!exec) return [];
  return activePuzzleIds(s)
    .map((id) => exec.puzzles[id])
    .filter((p): p is PuzzleInstance => p !== undefined);
}

/** The room the team is in (the corridor counts as the room it leads to). */
export function currentRoom(s: GameState): RoomId | null {
  const exec = s.exec;
  if (!exec) return null;
  const stop = currentStop(exec);
  if (!stop) return null;
  if (stop.kind === 'room') return stop.room;
  if (stop.kind === 'corridor') return stop.to;
  return stop.to;
}

export function isLooped(exec: ExecState): boolean {
  return exec.elapsedMs < exec.loopUntilMs;
}

export function isTunnel(exec: ExecState): boolean {
  return exec.elapsedMs < exec.tunnelUntilMs;
}

/** Guards see one tile less on the museum's main floor (GDD §10.2). */
export function dimVisionNow(s: GameState): boolean {
  const map = getMap(s.config.mapId);
  const room = currentRoom(s);
  if (map.dimVisionFloor === null || !room) return false;
  return map.rooms.find((r) => r.id === room)?.floor === map.dimVisionFloor;
}

export function genContext(s: GameState, rng: Rng, level: number, room: RoomId): GenContext {
  const map = getMap(s.config.mapId);
  return {
    rng,
    level,
    roles: presentRoles(s),
    players: puzzlePlayers(s),
    language: s.config.language,
    room,
    map,
    difficulty: s.config.difficulty,
    alarmTenths: s.alarm,
    guardDelta: map.guardDelta,
    dimVision:
      map.dimVisionFloor !== null &&
      map.rooms.find((r) => r.id === room)?.floor === map.dimVisionFloor,
  };
}

export function actContext(s: GameState, actor: PlayerId): ActContext {
  const exec = s.exec as ExecState;
  const me = playerOf(s, actor);
  return {
    actor,
    actorRole: me?.role ?? 'mastermind',
    players: puzzlePlayers(s),
    execMs: exec.elapsedMs,
    alarmTenths: s.alarm,
    looped: isLooped(exec),
    tunnel: isTunnel(exec),
    dimVision: dimVisionNow(s),
  };
}

export function tickContext(s: GameState): TickContext {
  const exec = s.exec as ExecState;
  return {
    execMs: exec.elapsedMs,
    alarmTenths: s.alarm,
    players: puzzlePlayers(s),
    roles: presentRoles(s),
    tunnel: isTunnel(exec),
    dimVision: dimVisionNow(s),
  };
}

/** Rounds half up: tenths × permille / 1000. */
export function scaleTenths(tenths: number, permille: number): number {
  return Math.floor((tenths * permille + 500) / 1000);
}

export type AlarmSource = 'error' | 'event' | 'drip' | 'gadget';

/**
 * Adds alarm (GDD §6). Errors are scaled by the difficulty; fixed amounts are not. Crossing
 * 50 % and 75 % has consequences; reaching 100 % fails the heist.
 */
export function addAlarm(ctx: Ctx, tenths: number, scaled: boolean, source: AlarmSource): number {
  const s = ctx.s;
  const exec = s.exec;
  if (!exec || tenths <= 0) return 0;
  const amount = scaled
    ? scaleTenths(tenths, DIFFICULTY[s.config.difficulty].penaltyPermille)
    : tenths;
  const before = s.alarm;
  s.alarm = Math.min(BALANCE.alarmMax, s.alarm + amount);
  const delta = s.alarm - before;
  // The public event says how much, not why: an event and a sabotage must look the same.
  emit(ctx, 'alarm', { alarm: s.alarm, delta, source: source === 'error' ? 'error' : 'other' });

  if (s.alarm >= BALANCE.policeAlertAt && !exec.policeAlerted) {
    exec.policeAlerted = true;
    emit(ctx, 'alarm.policeAlerted', {});
  }
  if (s.alarm >= BALANCE.optionalCloseAt && !exec.optionalClosed) {
    exec.optionalClosed = true;
    emit(ctx, 'alarm.optionalClosed', {});
  }
  if (s.alarm >= BALANCE.alarmMax) failHeist(ctx, 'alarm');
  return delta;
}

/** The heist is over without a hideout. */
export function failHeist(ctx: Ctx, reason: 'alarm' | 'time' | 'vault'): void {
  const s = ctx.s;
  if (s.result || s.phase === 'reveal' || s.phase === 'ended') return;
  s.result = computeResult(s, false, reason);
  emit(ctx, 'heist.failed', { reason });
  setPhase(ctx, 'reveal');
}

export function newPuzzleId(exec: ExecState): string {
  exec.puzzleCounter += 1;
  return `pz${exec.puzzleCounter}`;
}

/** Which role normally acts on a puzzle type (for choosing complementary puzzles in a room). */
export const SOLVER_OF: Record<PuzzleType, 'locksmith' | 'hacker' | 'team'> = {
  lock: 'locksmith',
  safe: 'locksmith',
  laser: 'locksmith',
  panel: 'hacker',
  cameras: 'hacker',
  password: 'hacker',
  corridor: 'team',
  escape: 'team',
  vault: 'team',
};
